import { useMemo, useState } from 'react';
import type { ColorSwatchesProps } from '@stitch-fix/mode-react';
import type { ShopProductItemFragment } from './getUngatedPaginatedShopCategory';
type ShopProductColorOption = ShopProductItemFragment['colorOptions'][number];
export type ShopCategorySwatchVariant = ShopProductColorOption['variant'];
type SwatchColorOption = {
  availability: ShopProductColorOption['inventory']['availability'];
  name: string;
  swatch: ShopProductColorOption['swatch'];
  uniqueColorId: string;
  variant: ShopCategorySwatchVariant;
};
type UseShopCategoryColorSwatchesProps = {
  shopProduct: ShopProductItemFragment;
};
export const useShopCategoryColorSwatches = ({
  shopProduct
}: UseShopCategoryColorSwatchesProps) => {
  const {
    swatchColorOptions,
    initialColorId
  } = useMemo(() => {
    const colorIdToSwatchColorOptionMap = new Map<string, SwatchColorOption>();
    shopProduct.colorOptions?.forEach(colorOption => {
      if (colorOption.uniqueColorId) {
        colorIdToSwatchColorOptionMap.set(colorOption.uniqueColorId, {
          availability: colorOption.inventory?.availability,
          name: colorOption.name,
          uniqueColorId: colorOption.uniqueColorId,
          swatch: colorOption.swatch,
          variant: colorOption.variant
        });
      }
    });
    const swatchOptions = Array.from(colorIdToSwatchColorOptionMap.values());
    const preferredId = shopProduct.variantBySkuId?.color.uniqueColorId;
    const preferredOption = preferredId ? colorIdToSwatchColorOptionMap.get(preferredId) : undefined;
    const firstInStock = swatchOptions.find(option => option.availability !== 'OUT_OF_STOCK');
    const fallback = swatchOptions[0];
    const initialId = (preferredOption && preferredOption.availability !== 'OUT_OF_STOCK' ? preferredOption.uniqueColorId : firstInStock?.uniqueColorId || fallback?.uniqueColorId) ?? '';
    return {
      swatchColorOptions: swatchOptions,
      initialColorId: initialId
    };
  }, [shopProduct]);
  const [selectedColorId, setSelectedColorId] = useState(initialColorId);
  const [previewColorId, setPreviewColorId] = useState<string | undefined>();
  const activeSelection = swatchColorOptions.some(option => option.uniqueColorId === selectedColorId) ? selectedColorId : initialColorId;
  const currentOption = useMemo(() => swatchColorOptions.find(option => option.uniqueColorId === (previewColorId || activeSelection)), [swatchColorOptions, previewColorId, activeSelection]);
  const colorSwatchesProps: ColorSwatchesProps = {
    colors: swatchColorOptions.map(option => ({
      label: option.name,
      uniqueColorId: option.uniqueColorId,
      isOutOfStock: option.availability === 'OUT_OF_STOCK',
      hex: option.swatch.hex,
      url: option.swatch.url ?? ''
    })),
    limit: {
      sm: 3,
      md: 4
    },
    onChange: (_, id) => id && setSelectedColorId(id),
    onPreview: setPreviewColorId,
    onPreviewEnd: () => setPreviewColorId(undefined),
    selectedColor: activeSelection,
    size: 'small'
  };
  return {
    colorSwatchesProps,
    displayedAvailability: currentOption?.availability,
    displayedVariant: currentOption?.variant
  };
};